<template>
  <div v-if="show" class="import-resume-page" :class="showFooter && 'show-footer'">
    <div class="info" :class="onlyUploadResume && 'only-upload'">
      {{ importResumeTip }}
    </div>
    <div class="upload-resume-btn">
      <van-uploader
        :before-read="beforeRead"
        :after-read="upload"
        :accept="acceptType"
      >
        <van-button class="upload-btn">
          <van-icon class="icon iconfont icona-UploadMyResume"/>
          <span class="label">{{ $t(`${i18nPrefix}.btn.uploadResume`) }}</span>
        </van-button>
      </van-uploader>
    </div>
    <div v-if="showCreateNewBtn || (!needUploadResume && !onlyUploadResume)">
      <div class="or">
        {{ $t(`${i18nPrefix}.label.or`) }}
      </div>
      <van-button class="upload-btn create-new-btn" @click="createNewResume">
        <van-icon class="icon iconfont icona-CreateNewResume"/>
        <span class="label">{{ $t(`${i18nPrefix}.btn.${personalInfo.profile_step > 1 ? 'editProfile' : 'createNewResume'}`) }}</span>
      </van-button>
    </div>
    <div class="or">
      {{ $t(`${i18nPrefix}.label.or`) }}
    </div>
    <div class="third-resume-btn">
      <van-button class="upload-btn third-btn" @click="handleThirdImport(THIRD_PARTY_CHANNEL_TYPE_51)">
        <van-icon class="icon iconfont icona-51Job"/>
        <span class="label">{{ $t(`${i18nPrefix}.btn.importJob51`) }}</span>
      </van-button>
      <van-button class="upload-btn third-btn" @click="handleThirdImport(THIRD_PARTY_CHANNEL_TYPE_JOBSTREET)">
        <van-icon class="icon iconfont iconGongzuojie"/>
        <span class="label">{{ $t(`${i18nPrefix}.btn.importJobStreet`) }}</span>
      </van-button>
    </div>
    <div v-if="showFooter" class="footer">
      <div class="footer-btn-group">
        <van-button v-if="!hiddenBackBtn" class="back-btn footer-btn" @click="goBack">
          {{ $t(`common.btn.back`) }}
        </van-button>
        <van-button v-if="!hiddenSkipBtn" class="skip-btn footer-btn" @click="goSkip">
          {{ $t(`${i18nPrefix}.btn.skip`) }}
        </van-button>
      </div>
      <div class="footer-background"></div>
    </div>
    <AIResumeParseLoadingDialog :isShow="showAIResumeParseDialog" isMobile/>
  </div>
</template>

<script>
// import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import { STEP_TYPE } from '@/constant/candidate'
import {
  THIRD_PARTY_CHANNEL_TYPE_51, THIRD_PARTY_CHANNEL_TYPE_FACEBOOK,
  THIRD_PARTY_CHANNEL_TYPE_LINKEDIN, THIRD_PARTY_CHANNEL_TYPE_JOBSTREET,
  THIRD_PARTY_LOGIN_STATUS_MAPPING
} from '@/constant/thirdParty'
import mixin from './mixin'
import { deviceType } from '@/utils/common'
import { generateTempFileKey, getFileExtension, uploadFile } from '@/utils/oss'
import AIResumeParseLoadingDialog from '@/components/dialog/aiResumeParseLoadingDialog.vue'
import { AI_PARSE_MAX_COUNT, MAX_UPLOAD_RESUME_FILE_SIZE } from '@/constant/application'

const RESUME_ACCEPT_TYPE = 'application/msword,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,message/rfc822,image/*'
const ANDROID_RESUME_ACCEPT_TYPE = '*/*'

export default {
  components: {
    AIResumeParseLoadingDialog
  },
  mixins: [mixin],
  props: {
    needUploadResume: {
      type: Boolean,
      default: false
    },
    personalInfo: {
      type: Object
    },
    companyId: {
      required: false
    }
  },
  data () {
    return {
      THIRD_PARTY_CHANNEL_TYPE_51,
      THIRD_PARTY_CHANNEL_TYPE_FACEBOOK,
      THIRD_PARTY_CHANNEL_TYPE_LINKEDIN,
      THIRD_PARTY_CHANNEL_TYPE_JOBSTREET,
      THIRD_PARTY_LOGIN_STATUS_MAPPING,
      i18nPrefix: 'admin.application.candidate',
      acceptType: RESUME_ACCEPT_TYPE,
      acceptTypePhoto: 'image/*',
      uploadTask: {
        taskId: null,
        finished: false,
        percentage: 0
      },
      authUrl: {
        linkedinAuthUrl: ''
      },
      canThirdPartyLogin: false,
      showThirdPartyLoading: false,
      showAIResumeParseDialog: false,
      parseCount: 1
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapGetters('company', ['companyThemeSetting']),
    currentAccount () {
      return this.getCurrentAccount
    },
    useAIResumeParse () {
      return this.companyThemeSetting?.company.has_ai_resume_parse_premium
    },
    showThirdParty () {
      if (this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME]?.showThirdParty) {
        return true
      }
      return false
    },
    onlyUploadResume () {
      if (this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME]?.onlyUploadResume) {
        return true
      }
      return false
    },
    skipParseResume () {
      if (this.resumeEditorSetting?.skipParseResume) {
        return true
      }
      return false
    },
    showCreateNewBtn () {
      return !!this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME].showCreateNewBtn
    },
    showFooter () {
      return !!this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME].showFooter
    },
    hiddenBackBtn () {
      return !!this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME].hiddenBackBtn
    },
    hiddenSkipBtn () {
      return !!this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME].hiddenSkipBtn
    },
    importResumeTip () {
      const resumeTip = this.resumeEditorSetting?.[STEP_TYPE.UPLOAD_RESUME].importResumeTip
      return resumeTip
    }
  },
  created () {
    if (this.showThirdParty) {
      this.initThirdPartyLoginUrl()
    }
    if (deviceType.isAndroid) {
      this.acceptType = ANDROID_RESUME_ACCEPT_TYPE
    } else if (!deviceType.isiOS) {
      this.acceptType = 'application/*'
    }
  },
  mounted () {},
  methods: {
    ...mapActions('personal', [
      'getResumeFromThirdParty',
      'checkLoginStatusForThirdParty',
      'parseResumeFromThirdParty',
      'getUrlToImportThirdPartyResume',
      'personalUploadResume',
      'parseCandidateResumeByCandidate',
      'createParseResumeInfoByCandidateTask',
      'getParseResumeInfoByCandidateTaskStatus'
    ]),
    async initThirdPartyLoginUrl () {
      this.$emit('load', true)
      try {
        const resp = await this.getUrlToImportThirdPartyResume()
        if (resp && resp.linkedin_auth_url) {
          this.authUrl.linkedinAuthUrl = resp.linkedin_auth_url
        }
      } finally {
        this.$emit('load', false)
      }
    },
    handleThirdImport (type) {
      this.$emit('handleThirdImport', type)
    },
    async handUploadResume (file) {
      let jobId = null
      const fileExt = getFileExtension(file)
      const fileKey = generateTempFileKey(fileExt)
      if (this.jobId && this.jobId !== '-1') {
        jobId = this.jobId
      }
      this.uploading = true
      this.$emit('load', true)
      try {
        await uploadFile(file, fileKey)
      } finally {
        this.uploading = false
        this.$emit('load', false)
      }
      if (this.onlyUploadResume && this.skipParseResume) {
        this.$emit('uploadResume', fileKey, this.skipParseResume)
        return
      }
      if (this.useAIResumeParse) {
        this.showAIResumeParseDialog = true
      } else {
        this.$eventBus.$emit('showLoading', true)
      }
      try {
        const resumeInfo = await this.parseResume(fileKey, jobId)
        if (resumeInfo) {
          if (this.onlyUploadResume) {
            this.$emit('uploadResume', resumeInfo, this.skipParseResume)
            return
          }
          this.uploadedResume = true
          this.$emit('change', resumeInfo)
          this.createNewResume()
        }
      } finally {
        if (this.useAIResumeParse) {
          this.showAIResumeParseDialog = false
        } else {
          this.$eventBus.$emit('showLoading', false)
        }
      }
    },
    async parseResume (fileKey, jobId) {
      const resp = await this.createParseResumeInfoByCandidateTask({ resume_file: fileKey, job_id: jobId, company_id: this.companyId })
      if (resp) {
        const taskId = resp.task_id
        this.parseCount = 1
        const resumeInfo = await this.getParseResumeInfo(taskId)
        return resumeInfo
      }
    },
    async getParseResumeInfo (taskId) {
      if (this.parseCount > AI_PARSE_MAX_COUNT) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.aiParseResumeTimeout`))
        return
      }
      const resp = await this.getParseResumeInfoByCandidateTaskStatus({ task_id: taskId })
      if (resp) {
        const status = resp.status
        if (status === 'PENDING') {
          return new Promise((resolve) => {
            setTimeout(async () => {
              const result = await this.getParseResumeInfo(taskId)
              resolve(result)
            }, 1000)
          })
        } else if (status === 'SUCCESS') {
          return resp.result
        } else {
          if (this.useAIResumeParse) {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.aiParseResumeError`))
          } else {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.parseResumeError`))
          }
        }
      }
    },
    beforeRead (file) {
      const fileName = file.name
      const lastDotIndex = fileName.lastIndexOf('.')
      const ext = lastDotIndex > -1 ? fileName.slice(lastDotIndex) : ''
      if (['.jpeg', '.png', '.bmp', '.jpg', '.doc', '.docx', '.pdf', '.html', '.mht'].includes(ext.toLowerCase()) && file.size <= MAX_UPLOAD_RESUME_FILE_SIZE) {
        return true
      } else {
        this.$toast({ message: this.$t(`${this.i18nPrefix}.message.uploadTips`) })
        return false
      }
    },
    upload (fileInfo) {
      const file = fileInfo.file
      this.handUploadResume(file)
    },
    onBackBtnClick () {
      const back = this.$route.query.back
      if (back) {
        mixedRouterPush(back)
        return
      }
      this.$router.go(-1)
    },
    async onLoginBtnClick () {
      if (!this.agree) {
        this.$toast({ message: this.$t('login.common.message.agreePolicyFirst') })
        return
      }
      const hasError = this.validate()
      if (hasError) {
        return
      }
      this.canThirdPartyLogin = false
      this.showThirdPartyLoading = true
      const resp = await this.getResumeFromThirdParty(this.thirdPartyForm)
      if (resp && resp.data) {
        if (resp.status === 'status') {
          this.thirdPartyTask.taskId = resp.data
          this.thirdPartyTask.status = resp.status
          this.thirdPartyTask.thirdparty = this.thirdPartyForm.thirdparty
          this.checkThirdPartyStatus()
        } else if (resp.status === 'failed' && resp.data?.errmsg) {
          if (THIRD_PARTY_LOGIN_STATUS_MAPPING[resp.data.errmsg]) {
            this.$toast({ message: this.$t(`${this.thirdPartyI18nPrefix}.message.${THIRD_PARTY_LOGIN_STATUS_MAPPING[resp.data.errmsg].i18nKey}`) })
          } else {
            this.$toast({ message: this.$t(`${this.i18nPrefix}.message.thirdPartyLoginError`) })
          }
          this.canThirdPartyLogin = true
          this.showThirdPartyLoading = false
        } else {
          this.$toast({ message: this.$t(`${this.i18nPrefix}.message.thirdPartyLoginError`) })
          this.canThirdPartyLogin = true
          this.showThirdPartyLoading = false
        }
      }
    },
    async checkThirdPartyStatus () {
      if (this.thirdPartyTask.thirdparty && this.thirdPartyTask.taskId) {
        this.thirdPartyTask.times++
        const resp = await this.checkLoginStatusForThirdParty({
          tid: this.thirdPartyTask.taskId,
          thirdparty: this.thirdPartyTask.thirdparty
        })
        if (resp && resp.status === 'status') {
          if (this.thirdPartyTask.times > 30) {
            this.cleanThirdPartyStatus()
            this.$toast({ message: this.$t(`${this.thirdPartyI18nPrefix}.message.loadResumeFailedLater`) })
            this.canThirdPartyLogin = true
            this.showThirdPartyLoading = false
            return
          }
          setTimeout(this.checkThirdPartyStatus, 1000)
        } else if (resp.status === 'failed' && resp.data?.errmsg) {
          if (THIRD_PARTY_LOGIN_STATUS_MAPPING[resp.data.errmsg]) {
            this.$toast({ message: this.$t(`${this.thirdPartyI18nPrefix}.message.${THIRD_PARTY_LOGIN_STATUS_MAPPING[resp.data.errmsg].i18nKey}`) })
          } else {
            this.$toast({ message: this.$t(`${this.thirdPartyI18nPrefix}.message.thirdPartyLoginError`) })
          }
          this.canThirdPartyLogin = true
          this.showThirdPartyLoading = false
        } else if (resp.status === 'ok' && resp.data) {
          const resumeList = resp.data
          if (resumeList && resumeList.length > 0) {
            this.importResumeSelectFrom.resumeList = resumeList
            const cnUrl = resumeList[0].cn_resume_url
            const enUrl = resumeList[0].en_resume_url
            this.importResumeSelectFrom.selectUrl = cnUrl || enUrl
          }
          this.importResumeDialog.show = true
          this.canThirdPartyLogin = true
          this.showThirdPartyLoading = false
        }
      }
    },
    goBack () {
      this.$emit('back')
    },
    goSkip () {
      this.$emit('skip')
    },
    createNewResume () {
      this.$emit('create')
    }
  }

}
</script>

<style lang="scss" scoped>
.import-resume-page {
  padding: 5px 18px 10px;

  &.show-footer {
    padding-bottom: 106px;
  }

  .info {
    margin-bottom: 16px;
    font-size: 16px;
    color: #606266;
  }

  .only-upload {
    margin-top: 16px;
  }

  .upload-btn {
    display: block;
    margin-bottom: 8px;
    padding-right: 0;
    width: 244px;
    height: 36px;
    color: #606266;
    border-color: #DCDFE6;

    .icon {
      position: relative;
      color: var(--custom-color, #0096cc);
      margin-right: 4px;
      font-size: 20px;
      height: 22px;
      line-height: 22px;
      vertical-align: middle;
    }

    ::v-deep {
      .van-button__content {
        justify-content: flex-start;
      }
    }
  }

  .upload-resume-btn {
    display: flex;
    flex-direction: column;
    align-items: center;

    ::v-deep {
      .van-button__text {
        color: #606266;
        font-size: 14px;

        .label {
          vertical-align: middle;
        }
      }
    }

    .van-uploader {
      &:last-child {
        ::v-deep {
          .upload-btn {
            margin-bottom: 0
          }
        }
      }
    }
  }

  .third-resume-btn {
    display: flex;
    flex-direction: column;
    align-items: center;

    ::v-deep {
      .van-button__text {
        color: #606266;
        font-size: 14px;

        .label {
          vertical-align: middle;
        }
      }
    }

    .third-btn {
      &:last-child {
        margin-bottom: 0
      }
    }
  }

  .or {
    margin: 12px 0;
    font-size: 14px;
    color: #909399;
    line-height: 20px;
    text-align: center;
    font-style: normal;
  }

  .create-new-btn {
    display: block;
    margin: 0 auto 8px;
    width: 244px;
    height: 36px;
    font-size: 14px;
    text-align: center;
  }

  .footer {
    margin: 0 -18px;
    width: 100%;
    position: fixed;
    bottom: 0;
    background-color: #fff;
    padding-bottom: constant(safe-area-inset-bottom);
    padding-bottom: env(safe-area-inset-bottom);

    .footer-btn-group {
      display: flex;
      border-top: 1px solid #c7c7c7;
      padding: 20px 18px;
    }

    .footer-btn {
      font-size: 14px;
      line-height: 15px;
      height: 24px;
      width: 50px;
      padding: 5px;
      text-align: center;
      border-color: var(--custom-color, #0096cc);
    }

    .back-btn {
      color: var(--custom-color, #0096cc);
    }

    .skip-btn {
      margin-left: auto;
      color: #fff;
      background-color: var(--custom-color, #0096cc);
    }

    .footer-background {
      background-color: var(--custom-color, #0096cc);
      height: 20px;
    }
  }
}
</style>
